/**
 * wordfirst sec 5 — trend decision rules (within-person only).
 *
 * Reference point = the FIRST scan, fixed, never rolling. Scale 0-100, uniform
 * across domains (SENSE is dB but follows the same principle). A trend needs >=2
 * scans; the card is full at the second.
 *
 * Declared asymmetry (spec sec 5): encourage fast, warn slow. One good crossing
 * is enough (firstSign at +5 in a single scan); a bad direction never fires on a
 * single scan and only becomes "notYetImproving" after two consecutive scans are
 * >=10 the wrong way. So the second scan can never yield a negative word.
 *
 * This is a DISPLAY change only. It MUST NOT bump engine_version — that would
 * reset every existing trend series.
 */

export type TrendState =
  | 'firstSign'
  | 'improving'
  | 'steady'
  | 'notYetImproving'
  | 'singleScan'
  | 'notMeasured' // gated on measurement package (status/value:null)
  | 'newBaseline'; // gated on measurement package (engine_version)

export interface TrendStatus {
  state: TrendState;
  /** state word colour family: green = good movement, amber = slow, blue = steady. */
  color: 'green' | 'amber' | 'blue';
  /** signed points vs the fixed first scan, oriented so + = the good direction. */
  goodDelta: number;
  /** magnitude vs first (raw, absolute) — drives the "N points lower/higher" word. */
  magnitude: number;
  /** raw direction vs first: -1 lower, 0 same, +1 higher (picks lower/higher word). */
  rawDir: -1 | 0 | 1;
}

/** Change we flag from (spec sec 5): 5 = first sign / sustained, 10 = confirmed. */
export const TREND_FIRST_SIGN = 5;
export const TREND_CONFIRMED = 10;

/**
 * Compute the trend state from a time-ordered series (oldest -> newest) and the
 * metric's direction. `lowerIsBetter` for Hormonal Imprint + symptom load; false
 * for THINK / SENSE (higher is better).
 */
export function computeTrendStatus(
  values: number[],
  lowerIsBetter: boolean
): TrendStatus {
  const n = values.length;
  if (n < 2) {
    return { state: 'singleScan', color: 'blue', goodDelta: 0, magnitude: 0, rawDir: 0 };
  }
  const first = values[0];
  const current = values[n - 1];
  const prev = values[n - 2];

  // orient so a positive delta is always the GOOD direction
  const goodOf = (v: number) => (lowerIsBetter ? first - v : v - first);
  const currentGood = goodOf(current);
  const prevGood = goodOf(prev);

  const rawDelta = current - first;
  const magnitude = Math.abs(rawDelta);
  const rawDir: -1 | 0 | 1 = rawDelta < 0 ? -1 : rawDelta > 0 ? 1 : 0;

  let state: TrendState;
  if (
    currentGood >= TREND_CONFIRMED ||
    (currentGood >= TREND_FIRST_SIGN && prevGood >= TREND_FIRST_SIGN)
  ) {
    // >=10 the good way, OR >=5 held across two consecutive scans
    state = 'improving';
  } else if (currentGood <= -TREND_CONFIRMED && prevGood <= -TREND_CONFIRMED) {
    // >=10 the wrong way, sustained over two consecutive scans (no fast path)
    state = 'notYetImproving';
  } else if (currentGood >= TREND_FIRST_SIGN) {
    // first crossing of +5 in a single scan — shown immediately, never taken back
    state = 'firstSign';
  } else {
    // everything else, incl. a single wrong-way scan (asymmetry: stays steady)
    state = 'steady';
  }

  const color: TrendStatus['color'] =
    state === 'improving' || state === 'firstSign'
      ? 'green'
      : state === 'notYetImproving'
        ? 'amber'
        : 'blue';

  return { state, color, goodDelta: currentGood, magnitude, rawDir };
}
